import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = format(new Date(), 'yyyy-MM-dd');

const ESTADO_COLORS = {
  borrador: { bg: '#FEF3C7', color: '#92400E', label: 'Borrador' },
  enviado:  { bg: '#D1FAE5', color: '#065F46', label: 'Enviado ✓' },
  recibido: { bg: '#DBEAFE', color: '#1E40AF', label: 'Recibido ✓' },
};
const CAT_INFO = {
  empaques: { label: 'Empaques', icon: '🛍️' }, aseo: { label: 'Aseo', icon: '🧹' },
  utensilios: { label: 'Utensilios', icon: '🔧' }, insumos: { label: 'Insumos', icon: '📦' },
  ingredientes: { label: 'Ingredientes', icon: '🧪' }, papeleria: { label: 'Papelería', icon: '📄' },
};

function Pill({ activo, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
      border: `2px solid ${activo ? '#1a1a2e' : '#E5E7EB'}`,
      background: activo ? '#1a1a2e' : 'white', color: activo ? 'white' : '#374151',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</button>
  );
}

function ItemInput({ item, valor, onChange }) {
  const activo = parseFloat(valor) > 0;
  return (
    <div style={{ background: 'white', border: `2px solid ${activo ? '#6366F1' : '#E5E7EB'}`, borderRadius: 10, padding: '9px 10px' }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.nombre}</div>
      <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 6 }}>{item.unidad_medida}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <button onClick={() => onChange(Math.max(0, (parseFloat(valor) || 0) - 1))} style={{ width: 26, height: 26, border: '2px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>−</button>
        <input type="number" min="0" value={valor} onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
          style={{ flex: 1, textAlign: 'center', padding: '4px', border: '2px solid #E5E7EB', borderRadius: 6, fontSize: 14, fontWeight: 800, outline: 'none', width: 0 }} />
        <button onClick={() => onChange((parseFloat(valor) || 0) + 1)} style={{ width: 26, height: 26, border: '2px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>+</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /envios-general
// ─────────────────────────────────────────
export default function EnviosGeneral() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia', 'administracion'].includes(usuario?.rol);

  const [tab, setTab] = useState('nuevo');
  const [bodegas, setBodegas] = useState([]);
  const [items, setItems] = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [bodegaNombre, setBodegaNombre] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [detalle, setDetalle] = useState({});
  const [observacion, setObs] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [envioDetalle, setEnvioDetalle] = useState(null);
  const [envioEditar, setEnvioEditar] = useState(null);
  const [invPDV, setInvPDV] = useState({ bodegas: [], items: [] });
  const [cargandoInv, setCargandoInv] = useState(false);
  const [bodegaAbierta, setBodegaAbierta] = useState(null);

  useEffect(() => {
    Promise.all([
      API.get('/bodegas'),
      API.get('/inventario/items', { params: { tipo: 'semanal' } }),
      API.get('/inventario/items', { params: { tipo: 'diario' } }),
    ]).then(([b, itSem, itDia]) => {
      const pvs = b.data.filter(x => x.tipo === 'punto_venta');
      setBodegas(pvs);
      const todos = [...itDia.data, ...itSem.data];
      const unicos = todos.filter((item, idx, arr) => arr.findIndex(x => x.nombre === item.nombre && x.tipo_inventario === item.tipo_inventario) === idx);
      setItems(unicos);
      if (pvs.length) { setBodegaId(String(pvs[0].id)); setBodegaNombre(pvs[0].nombre); }
    }).catch(() => {});
  }, []);

  const cambiarBodega = (b) => { setBodegaId(String(b.id)); setBodegaNombre(b.nombre); };

  useEffect(() => {
    if (tab !== 'historial') return;
    API.get('/envios-general', { params: { bodega_id: bodegaId || null } }).then(r => setHistorial(r.data)).catch(() => {});
  }, [tab, bodegaId]);

  useEffect(() => {
    if (tab !== 'inventario') return;
    setCargandoInv(true);
    API.get('/envios-general/inventario-pdv').then(r => setInvPDV(r.data)).catch(() => {}).finally(() => setCargandoInv(false));
  }, [tab]);

  const totalItems = Object.values(detalle).filter(v => parseFloat(v) > 0).length;

  const handleGuardar = async (enviar = false) => {
    if (!bodegaId) { toast.error('Selecciona un punto de venta'); return; }
    if (totalItems === 0) { toast.error('Agrega al menos un ítem'); return; }
    setGuardando(true);
    try {
      const det = items.filter(i => parseFloat(detalle[i.id]) > 0).map(i => ({ item_id: i.id, cantidad: parseFloat(detalle[i.id]) }));
      await API.post('/envios-general', { bodega_id: bodegaId, fecha, items: det, observacion, enviar });
      toast.success(enviar ? '✅ Envío registrado y confirmado' : '💾 Borrador guardado');
      setDetalle({}); setObs('');
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  };

  const handleCorregir = async (id) => {
    try {
      const r = await API.get(`/envios-general/${id}`);
      const envio = r.data;
      setBodegaId(String(envio.bodega_id || bodegaId));
      setBodegaNombre(envio.bodega_nombre || bodegaNombre);
      setFecha(envio.fecha ? envio.fecha.slice(0, 10) : hoy);
      setObs(envio.observacion || '');
      const det = {};
      (envio.detalle || []).forEach(d => { det[d.item_id] = d.cantidad; });
      setDetalle(det);
      setEnvioEditar(envio);
      setTab('nuevo');
      toast('✏️ Cargando envío para corrección...', { icon: '⚠️' });
    } catch { toast.error('Error cargando envío'); }
  };

  const handleGuardarCorreccion = async () => {
    if (totalItems === 0) { toast.error('Agrega al menos un ítem'); return; }
    setGuardando(true);
    try {
      const det = items.filter(i => parseFloat(detalle[i.id]) > 0).map(i => ({ item_id: i.id, cantidad: parseFloat(detalle[i.id]) }));
      await API.post('/envios-general/corregir', { envio_id: envioEditar.id, bodega_id: bodegaId, fecha, items: det, observacion });
      toast.success('✅ Envío corregido correctamente');
      setDetalle({}); setObs(''); setEnvioEditar(null);
      setTab('historial');
    } catch (e) { toast.error(e.response?.data?.error || 'Error al corregir'); }
    finally { setGuardando(false); }
  };

  const verDetalle = async (id) => {
    try { const r = await API.get(`/envios-general/${id}`); setEnvioDetalle(r.data); }
    catch { toast.error('Error cargando detalle'); }
  };

  const porCategoria = {};
  items.forEach(item => { const cat = item.categoria || 'otros'; if (!porCategoria[cat]) porCategoria[cat] = []; porCategoria[cat].push(item); });

  const mostrarBarra = tab === 'nuevo';

  return (
    <div style={{ paddingBottom: mostrarBarra ? 78 : 0 }}>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>📦 Envíos de Productos</h1>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>Producción → {bodegaNombre || 'PDV'}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto', paddingBottom: 4 }}>
        {bodegas.map(b => <Pill key={b.id} activo={String(bodegaId) === String(b.id)} onClick={() => cambiarBodega(b)}>{b.nombre}</Pill>)}
      </div>

      <div style={{ marginBottom: 12 }}>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
          style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        <Pill activo={tab === 'nuevo'} onClick={() => { setTab('nuevo'); setEnvioDetalle(null); }}>📦 Nuevo</Pill>
        <Pill activo={tab === 'historial'} onClick={() => { setTab('historial'); setEnvioDetalle(null); setEnvioEditar(null); }}>📋 Historial</Pill>
        <Pill activo={tab === 'inventario'} onClick={() => setTab('inventario')}>🔍 Inventario PDV</Pill>
      </div>

      {tab === 'inventario' ? (
        cargandoInv ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando inventarios...</div>
        ) : (
          <div>
            <div style={{ marginBottom: 12, fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
              Último inventario enviado por PDV — úsalo para decidir qué enviar.
            </div>
            {(invPDV.bodegas || []).map(({ bodega, ultimo_registro, detalle: det }) => {
              const abierta = bodegaAbierta === bodega.id;
              const itemsConStock = det.filter(d => {
                const val = d.cantidad;
                if (val === null || val === undefined) return false;
                if (typeof val === 'string') return val !== '' && val !== '0';
                return parseFloat(val) > 0;
              });
              const porCat = {};
              det.forEach(d => { const cat = d.categoria || 'otros'; if (!porCat[cat]) porCat[cat] = []; porCat[cat].push(d); });
              return (
                <div key={bodega.id} style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 10 }}>
                  <div style={{ background: '#1a1a2e', padding: '10px 14px', cursor: 'pointer' }} onClick={() => setBodegaAbierta(abierta ? null : bodega.id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>🏪 {bodega.nombre}</div>
                      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>{abierta ? '▲' : '▼'}</span>
                    </div>
                    {ultimo_registro ? (
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, marginTop: 3 }}>
                        {format(new Date((ultimo_registro.fecha||'').slice(0,10) + 'T12:00'), "d MMM yyyy", { locale: es })} · {itemsConStock.length} con stock
                      </div>
                    ) : <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 3 }}>Sin inventario enviado</div>}
                  </div>
                  {abierta && (
                    <div style={{ padding: '8px 12px' }}>
                      {det.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, padding: '10px 0' }}>Sin datos</div>
                      ) : Object.entries(porCat).map(([cat, catItems]) => {
                        const catInfo = CAT_INFO[cat] || { label: cat, icon: '📋' };
                        const conValor = catItems.filter(d => d.cantidad !== null && d.cantidad !== undefined);
                        if (!conValor.length) return null;
                        return (
                          <div key={cat} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>{catInfo.icon} {catInfo.label}</div>
                            {conValor.map(d => {
                              const esSiNo = d.unidad_medida?.toUpperCase().includes('SI/NO');
                              const stockBajo = !esSiNo && parseFloat(d.cantidad) < 10;
                              return (
                                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F9FAFB', fontSize: 11 }}>
                                  <span style={{ color: '#374151' }}>{d.item_nombre}</span>
                                  <span style={{ fontWeight: 700, color: esSiNo ? (d.cantidad === 'SI' ? '#065F46' : '#991B1B') : stockBajo ? '#DC2626' : '#1a1a2e' }}>
                                    {esSiNo ? d.cantidad : `${d.cantidad} ${d.unidad_medida}`}{stockBajo && ' ⚠️'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : tab === 'historial' ? (
        envioDetalle ? (
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>📦 Envío #{envioDetalle.id} — {envioDetalle.bodega_nombre}</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {envioDetalle.fecha ? format(new Date(envioDetalle.fecha.slice(0,10)+'T12:00'), "d 'de' MMMM yyyy", { locale: es }) : '—'}
                </div>
              </div>
              <button onClick={() => setEnvioDetalle(null)} style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>← Volver</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {(envioDetalle.detalle || []).map(d => (
                <div key={d.item_id} style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#1a1a2e' }}>{d.cantidad}</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>{d.unidad_medida}</div>
                  <div style={{ fontWeight: 700, fontSize: 11, color: '#374151', marginTop: 3 }}>{d.item_nombre}</div>
                </div>
              ))}
            </div>
            {envioDetalle.observacion && (
              <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#065F46', fontWeight: 600, marginTop: 10 }}>📝 {envioDetalle.observacion}</div>
            )}
          </div>
        ) : historial.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600 }}>Sin envíos registrados</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historial.map(h => {
              const ec = ESTADO_COLORS[h.estado] || ESTADO_COLORS.borrador;
              return (
                <div key={h.id} style={{ background: 'white', borderRadius: 14, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{h.bodega_nombre}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                        {h.fecha ? format(new Date(h.fecha.slice(0,10)+'T12:00'), "d MMM yyyy", { locale: es }) : '—'} · {h.total_items} ítems
                      </div>
                    </div>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontWeight: 700, fontSize: 11, background: ec.bg, color: ec.color }}>{ec.label}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button onClick={() => verDetalle(h.id)} style={{ padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11 }}>Ver</button>
                    {esAdmin && <button onClick={() => handleCorregir(h.id)} style={{ padding: '4px 10px', border: '1px solid #F59E0B', borderRadius: 6, background: '#FFFBEB', color: '#92400E', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>✏️ Corregir</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <>
          {envioEditar && (
            <div style={{ background: '#FEF3C7', border: '2px solid #F59E0B', borderRadius: 12, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#92400E', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span>✏️ Corrigiendo envío #{envioEditar.id}</span>
              <button onClick={() => { setEnvioEditar(null); setDetalle({}); setObs(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#92400E' }}>✕</button>
            </div>
          )}
          {totalItems > 0 && (
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '9px 14px', marginBottom: 12, fontSize: 12, color: '#1D4ED8', fontWeight: 600 }}>
              📦 {totalItems} tipo{totalItems > 1 ? 's' : ''} de ítem seleccionado{totalItems > 1 ? 's' : ''}
            </div>
          )}

          {Object.entries(porCategoria).map(([cat, catItems]) => {
            const catInfo = CAT_INFO[cat] || { label: cat, icon: '📋' };
            return (
              <div key={cat} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 14 }}>{catInfo.icon}</span>
                  <span style={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#1a1a2e' }}>{catInfo.label}</span>
                  <span style={{ fontSize: 10, color: '#9CA3AF' }}>({catItems.length})</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {catItems.map(item => (
                    <ItemInput key={item.id} item={item} valor={detalle[item.id] ?? ''} onChange={v => setDetalle(prev => ({ ...prev, [item.id]: v }))} />
                  ))}
                </div>
              </div>
            );
          })}

          <div style={{ background: 'white', borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>📝 Observación (opcional)</label>
            <textarea value={observacion} onChange={e => setObs(e.target.value)} rows={2} placeholder="Alguna nota sobre el envío..."
              style={{ width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
        </>
      )}

      {mostrarBarra && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(70px + env(safe-area-inset-bottom, 0px))', display: 'flex', gap: 10, padding: '10px 20px', background: 'white', borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)', zIndex: 90 }}>
          {envioEditar ? (
            <button onClick={handleGuardarCorreccion} disabled={guardando || totalItems === 0}
              style={{ flex: 1, padding: '13px 0', border: 'none', borderRadius: 12, background: totalItems === 0 ? '#E5E7EB' : '#D97706', color: totalItems === 0 ? '#9CA3AF' : 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
              {guardando ? '⏳...' : '✅ Guardar corrección'}
            </button>
          ) : (
            <>
              <button onClick={() => handleGuardar(false)} disabled={guardando}
                style={{ flex: 1, padding: '13px 0', border: '2px solid #E5E7EB', borderRadius: 12, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                {guardando ? '⏳' : '💾 Borrador'}
              </button>
              <button onClick={() => handleGuardar(true)} disabled={guardando || totalItems === 0}
                style={{ flex: 2, padding: '13px 0', border: 'none', borderRadius: 12, background: totalItems === 0 ? '#E5E7EB' : '#1a1a2e', color: totalItems === 0 ? '#9CA3AF' : 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                {guardando ? '⏳...' : '✅ Confirmar envío'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
